import type { ComponentType } from "react"

import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"

import type {
  ContractBlockerCodeOf,
  ContractFlavor,
  ProposalContractReadinessDto,
} from "~/api/dto/proposal-contract.dto"

export type ContractSendVariables = {
  proposalId: string
  idempotencyKey: string
}

export type ContractBlockerFixProps<R extends ProposalContractReadinessDto> = {
  readonly proposalId: string
  readonly readiness: R
  /** Re-reads readiness once a fix command settles, success or failure. */
  readonly onSettled: () => void
}

/**
 * A blocker the drawer clears in place (Refresh pricing, Recalculate savings)
 * or with a kind-specific link. `available` decides the owner chip, so the
 * chip never promises agency the action does not offer.
 */
export type ContractInlineFix<R extends ProposalContractReadinessDto> = {
  readonly available: (readiness: R) => boolean
  readonly Action: ComponentType<ContractBlockerFixProps<R>>
}

/** The Setup step that clears a blocker, when the seller can act on it in Vision. */
export type ContractFixStep = {
  readonly path: string
  readonly label: string
}

export type ContractPartnerOverrideProps = {
  readonly proposalId: string
  readonly onClose: () => void
}

export type ContractLanguageFieldProps<R extends ProposalContractReadinessDto> =
  {
    readonly proposalId: string
    readonly readiness: R
  }

export type ContractProviderContractProps<
  R extends ProposalContractReadinessDto,
> = {
  readonly proposalId: string
  readonly readiness: R
}

/**
 * What a proposal kind (Battery-Only, Solar) plugs into the shared Contract
 * drawer: its readiness read and send command, already bound to its query keys
 * inside each hook, plus how each blocker is fixed, where Close returns, the
 * flavor names and the Package section's kind-specific fields. The drawer owns
 * the stage strip, headline, blocker rows, history and the send attempt.
 */
export type ContractKindBundle<R extends ProposalContractReadinessDto> = {
  readonly flavorLabel: Readonly<Record<ContractFlavor, string>>
  /**
   * A package of Powur's supplemental documents only, beside a main contract
   * the provider sends: its names, and whether the readiness's next package is
   * one. Absent, every package is named by its flavor.
   */
  readonly supplementalPackage?: {
    readonly label: Readonly<Record<ContractFlavor, string>>
    readonly isNext: (readiness: R) => boolean
  }
  readonly proposalPath: (proposalId: string) => string
  /** Close target from location state; only this kind's in-app paths pass. */
  readonly returnTo: (state: unknown, fallback: string) => string
  readonly useReadiness: (proposalId: string) => UseQueryResult<R>
  readonly useSend: () => UseMutationResult<R, unknown, ContractSendVariables>
  /** Fixes rendered under a blocker, by code. They win over `fixStep`. */
  readonly inlineFixes: Readonly<
    Partial<Record<ContractBlockerCodeOf<R>, ContractInlineFix<R>>>
  >
  readonly fixStep: (
    proposalId: string,
    code: ContractBlockerCodeOf<R>,
  ) => ContractFixStep | null
  /** Beside the Package total while stored pricing is out of date. */
  readonly PricingOutOfDateIndicator: ComponentType
  /** The Package section's Language value. */
  readonly LanguageField: ComponentType<ContractLanguageFieldProps<R>>
  /**
   * A partner-run drawer that replaces the Vision contract on the same route
   * (Battery's Concert application status), or null.
   */
  readonly usePartnerOverride: (
    proposalId: string | undefined,
  ) => ComponentType<ContractPartnerOverrideProps> | null
  /**
   * A main contract the provider sends itself (Concert's loan documents),
   * shown above everything Vision sends. Absent when Vision sends it all.
   */
  readonly ProviderMainContract?: ComponentType<ContractProviderContractProps<R>>
}

const packageLabel = <R extends ProposalContractReadinessDto>(
  bundle: ContractKindBundle<R>,
  flavor: ContractFlavor,
  supplemental: boolean,
): string =>
  (supplemental ? bundle.supplementalPackage?.label[flavor] : undefined) ??
  bundle.flavorLabel[flavor]

/** What the drawer calls the package the readiness would send next. */
export const contractPackageLabel = <R extends ProposalContractReadinessDto>(
  bundle: ContractKindBundle<R>,
  readiness: R,
  flavor: ContractFlavor,
): string =>
  packageLabel(
    bundle,
    flavor,
    bundle.supplementalPackage?.isNext(readiness) ?? false,
  )

/**
 * What the drawer calls a sent version. Named by what the version went out
 * as, so a version sent under an earlier Finance Plan keeps its name.
 */
export const contractHistoryLabel = <R extends ProposalContractReadinessDto>(
  bundle: ContractKindBundle<R>,
  sentAsSupplemental: boolean,
  flavor: ContractFlavor,
): string => packageLabel(bundle, flavor, sentAsSupplemental)

/**
 * Location state is attacker-influencable via crafted links, so only in-app
 * paths under `prefix` are accepted: no absolute URLs, protocol-relative
 * hosts, or escapes.
 */
export const isSafeReturnTo = (path: string, prefix: string): boolean =>
  path.startsWith(prefix) &&
  !path.includes(`://`) &&
  !path.includes(`\\`) &&
  !path.includes(`..`) &&
  !path.includes(`//`)

/** `returnTo` from launch state when it is a safe path under `prefix`. */
export const contractReturnTo = (
  state: unknown,
  fallback: string,
  prefix: string,
): string => {
  if (state != null && typeof state === `object` && `returnTo` in state) {
    const { returnTo } = state
    if (typeof returnTo === `string` && isSafeReturnTo(returnTo, prefix)) {
      return returnTo
    }
  }
  return fallback
}
